import { apiService } from './api';
import { POLLING_CONFIG } from '../config/constants';

/**
 * Assessment service
 */
export const assessmentService = {
  /**
   * Get user usage information
   * @param {string} token - Auth token
   * @returns {Promise<{remaining: number, monthlyLimit: number}>}
   */
  getUserUsage: async (token) => {
    return await apiService.get('/api/user/usage', token);
  },

  /**
   * Submit assessment request
   * @param {string} notes - Clinical notes
   * @param {string} token - Auth token
   * @param {string} caseId - Optional case ID to link assessment
   * @returns {Promise<{assessmentId: string, usage: Object}>}
   */
  generateAssessment: async (notes, token, caseId = null) => {
    const payload = { notes };
    if (caseId) {
      payload.caseId = caseId;
    }
    return await apiService.post('/api/assessment/generate', payload, token);
  },

  /**
   * Get assessment status
   * @param {string} assessmentId - Assessment ID
   * @param {string} token - Auth token
   * @returns {Promise<{status: string, results?: Object, error?: string}>}
   */
  getAssessmentStatus: async (assessmentId, token) => {
    return await apiService.get(`/api/assessment/${assessmentId}/status`, token);
  },

  /**
   * Poll for assessment completion
   * @param {string} assessmentId - Assessment ID
   * @param {string} token - Auth token
   * @param {Function} onComplete - Callback when completed
   * @param {Function} onError - Callback on error
   * @returns {Function} Stop polling function
   */
  pollAssessmentStatus: (assessmentId, token, onComplete, onError) => {
    let attempts = 0;
    let timeoutId = null;
    let stopped = false;

    const poll = async () => {
      if (stopped) return;

      attempts++;

      try {
        const data = await assessmentService.getAssessmentStatus(assessmentId, token);

        if (data.status === 'completed') {
          onComplete(data);
          return;
        } else if (data.status === 'failed') {
          onError(new Error(data.error || 'Assessment failed'));
          return;
        } else if (data.status === 'processing') {
          if (attempts >= POLLING_CONFIG.MAX_ATTEMPTS) {
            onError(new Error('Assessment is taking longer than expected. Please try refreshing the page later.'));
            return;
          }

          // Continue polling
          timeoutId = setTimeout(poll, POLLING_CONFIG.POLL_INTERVAL);
        }
      } catch (error) {
        if (attempts >= POLLING_CONFIG.MAX_RETRY_ATTEMPTS) {
          onError(new Error('Failed to check assessment status. Please try again.'));
        } else {
          // Retry
          timeoutId = setTimeout(poll, POLLING_CONFIG.RETRY_INTERVAL);
        }
      }
    };

    // Start polling
    poll();

    // Return function to stop polling
    return () => {
      stopped = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  },

  /**
   * Submit feedback
   * @param {Object} feedbackData - Feedback data
   * @param {string} token - Auth token
   * @returns {Promise<{bonusCredits?: number}>}
   */
  submitFeedback: async (feedbackData, token) => {
    return await apiService.post('/api/feedback', feedbackData, token);
  },

  /**
   * Link existing assessment to a case
   * @param {string} assessmentId - Assessment ID
   * @param {string} caseId - Case ID
   * @param {string} token - Auth token
   * @param {Object} editedResults - Optional: clinician-edited results
   * @returns {Promise<Object>} Link confirmation
   */
  linkAssessmentToCase: async (assessmentId, caseId, token, editedResults = null) => {
    const payload = { caseId };
    if (editedResults) {
      payload.editedResults = editedResults;
    }
    return await apiService.put(`/api/assessment/${assessmentId}/case`, payload, token);
  }
};

